import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { ArrowUpRight } from "lucide-react";
import { cta } from "@/lib/site";

export const CtaCloser = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const dashOffset = useTransform(scrollYProgress, [0.1, 0.6], [420, 0]);

  return (
    <section
      id="send"
      ref={ref}
      className="relative py-32 md:py-48 overflow-hidden"
      data-testid="cta-section"
    >
      <div className="max-w-[1440px] mx-auto px-6 md:px-12 grid grid-cols-12 gap-6 items-center">
        <div className="col-span-12 md:col-span-9 relative">
          <div className="postmark text-[#D93829] mb-8">
            <span className="w-1 h-1 rounded-full bg-[#D93829]" />
            Return Address
          </div>

          <motion.h2
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
            className="font-serif-display text-[#1C1C1A] text-[56px] sm:text-[80px] md:text-[112px] leading-[0.92] tracking-[-0.02em]"
          >
            Stand out in the{" "}
            <span className="italic text-[#D93829]">mailbox</span>. <br />
            Show up in the{" "}
            <span className="italic">memory</span>.
          </motion.h2>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9, delay: 0.15 }}
            className="mt-10 max-w-2xl text-[#3B3A36] text-lg md:text-xl leading-relaxed font-sans-body"
          >
            No credit card. No contract. No minimums. Just open Thanks.io, drop
            in an address, pick a design, and let the pen-arms do the rest —
            your first card is on us.
          </motion.p>

          <div className="mt-14 flex flex-wrap items-center gap-8">
            <a
              href={cta("closer")}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="closer-primary-cta"
              className="group relative inline-flex items-center gap-3 bg-[#D93829] text-white px-10 py-5 rounded-full text-sm uppercase tracking-[0.22em] font-semibold overflow-hidden"
            >
              <span className="relative z-10">Send your first card — free</span>
              <ArrowUpRight
                size={20}
                className="relative z-10 transition-transform duration-500 group-hover:translate-x-1 group-hover:-translate-y-1"
              />
              <span className="absolute inset-0 bg-[#1C1C1A] scale-x-0 origin-left group-hover:scale-x-100 transition-transform duration-500 ease-[cubic-bezier(0.22,1,0.36,1)]" />
            </a>

            {/* Handwritten arrow SVG */}
            <div className="relative flex items-center gap-4">
              <svg
                viewBox="0 0 180 80"
                className="w-[180px] h-[80px] text-[#D93829]"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.4"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <motion.path
                  d="M6 60 C 40 20, 80 60, 130 25"
                  strokeDasharray="420"
                  style={{ strokeDashoffset: dashOffset }}
                />
                <motion.path
                  d="M118 18 L134 24 L124 38"
                  strokeDasharray="420"
                  style={{ strokeDashoffset: dashOffset }}
                />
              </svg>
              <span className="font-hand text-[#D93829] text-3xl md:text-4xl -rotate-3">
                yes, this button!
              </span>
            </div>
          </div>
        </div>

        {/* Right: single hero postcard */}
        <div className="hidden md:block md:col-span-3 relative">
          <motion.div
            initial={{ opacity: 0, y: 40, rotate: 0 }}
            whileInView={{ opacity: 1, y: 0, rotate: 8 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
            className="polaroid w-[280px]"
          >
            <img
              src="https://images.unsplash.com/photo-1584382179644-9c11f8e6ee0c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzF8MHwxfHNlYXJjaHwzfHxwb3N0YWwlMjBzdGFtcCUyMG1haWx8ZW58MHx8fHwxNzg0MjMyNjczfDA&ixlib=rb-4.1.0&q=85"
              alt="Postcard"
              className="w-full h-[220px] object-cover"
            />
            <div className="mt-3 text-[11px] uppercase tracking-[0.2em] text-[#68655E]">
              Postage · Included
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default CtaCloser;
